import { fetchSyncStatus, runServiceOrderSync } from "../dataLayer/syncStatus";
import {
  ClientSyncRun,
  ServiceOrderSyncDetails,
  ServiceOrderSyncResult,
} from "../interfaces/SyncStatus";

/**
 * Sync Status manager (TAS-516). The page talks to this; nothing above the manager knows
 * an axios client exists.
 */
class SyncStatusManager {
  async getSyncStatus(days?: number): Promise<ClientSyncRun[]> {
    return fetchSyncStatus(days);
  }

  /**
   * Kicks the Vista SM work-order + scope sync and normalises every outcome into one
   * result, so the caller never has to distinguish a thrown transport error from a
   * `success:false` body.
   *
   * The 404 the API returns to non-NPM tenants is turned into a plain message rather than
   * left to surface as "Request failed with status code 404" — the button is gated on
   * `showServiceOrderSync` so it should be unreachable, but a stale config would
   * otherwise produce a meaningless popup.
   */
  async syncServiceOrders(): Promise<ServiceOrderSyncResult> {
    try {
      return await runServiceOrderSync();
    } catch (error: any) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      const message = status === 404
        ? "Service order sync is not configured for this client."
        : (data?.message || data?.errorMessage || error?.message || "Service order sync failed.");
      return { success: false, message, errorMessage: message };
    }
  }
}

/**
 * The counts a completed run is worth reading aloud, as `label: value` pairs. Zero-valued
 * lines are kept — "0 inserted" is the answer a dispatcher who just entered a work order
 * needs to see, and dropping it would leave them staring at a blank result wondering
 * whether the sync ran at all.
 */
export function summarizeServiceOrderSync(
  details: ServiceOrderSyncDetails | null | undefined
): { label: string; value: number }[] {
  if (!details) return [];
  return [
    { label: "Work orders read", value: details.rowsRead ?? 0 },
    { label: "Inserted", value: details.inserted ?? 0 },
    { label: "Updated", value: details.updated ?? 0 },
    { label: "Unchanged", value: details.unchanged ?? 0 },
    { label: "Deactivated", value: details.deactivated ?? 0 },
    { label: "Scopes inserted", value: details.scopesInserted ?? 0 },
    { label: "Scopes updated", value: details.scopesUpdated ?? 0 },
    { label: "Scopes deactivated", value: details.scopesDeactivated ?? 0 },
  ];
}

export default new SyncStatusManager();
